import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import DocumentCapture from '../components/KYC/DocumentCapture';
import ExtractedDataDisplay from '../components/KYC/ExtractedDataDisplay';
import { kycAPI } from '../services/api';

const steps = [
  { label: 'Introduction', step: 'intro', isIntro: true },
  { label: 'National ID Front', step: 'national_id_front', documentType: 'national_id_front' },
  { label: 'National ID Back', step: 'national_id_back', documentType: 'national_id_back' },
  { label: 'Selfie', step: 'selfie', documentType: 'selfie' }
];

const KYCFlow = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Document processing state
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [showExtractedData, setShowExtractedData] = useState(false);
  const [lastCapturedImageData, setLastCapturedImageData] = useState(null);

  // Require auth: redirect to login if no token
  useEffect(() => {
    if (!localStorage.getItem('auth_token')) {
      navigate('/login', { replace: true });
      return;
    }
  }, [navigate]);

  // Initialize KYC session
  useEffect(() => {
    if (localStorage.getItem('auth_token')) {
      initializeSession();
    }
  }, []);

  const initializeSession = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Starting KYC session...');
      const response = await kycAPI.startSession('health', 'individual');
      console.log('Session response:', response);

      // Handle different response structures
      const id = response.data?.sessionId ||
                 response.data?.session?.id ||
                 response.session?.id;

      if (!id) {
        console.error('No session ID in response:', response);
        throw new Error('Invalid session response - no ID found');
      }

      console.log('Session ID:', id);
      setSessionId(id);
    } catch (err) {
      console.error('Failed to initialize session:', err);
      console.error('Error details:', err.response);
      setError(err.response?.data?.error || err.message || 'Failed to start KYC session');
      // Redirect to login on 401
      if (err.response?.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        navigate('/login', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCapture = async (imageData, metadata) => {
    try {
      setLoading(true);
      setError(null);

      const currentStep = steps[activeStep];
      if (currentStep?.isIntro) return;

      // Upload document to backend (step_code and document_type match backend mock keys)
      const response = await kycAPI.uploadDocument(
        sessionId,
        currentStep.step,
        currentStep.documentType,
        imageData
      );

      // API returns { success, message, data: { document, extractedData, ... } }
      const payload = response?.data ?? response;
      setUploadedDocument(payload.document);
      setExtractedData(payload.extractedData ?? {});
      setLastCapturedImageData(imageData);
      setShowExtractedData(true);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.error || err.message || 'Failed to upload document');
      if (err.response?.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        navigate('/login', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCorrections = async (documentId, corrections) => {
    try {
      await kycAPI.submitCorrections(documentId, corrections);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        navigate('/login', { replace: true });
      }
      throw err;
    }
  };

  const handleContinue = () => {
    setActiveStep(prev => prev + 1);
    setShowExtractedData(false);
    setUploadedDocument(null);
    setExtractedData(null);
    setLastCapturedImageData(null);
  };

  const handleRetake = () => {
    setShowExtractedData(false);
    setUploadedDocument(null);
    setExtractedData(null);
    setLastCapturedImageData(null);
  };

  const currentStep = steps[activeStep];

  if (loading && !sessionId) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Initializing KYC session...</Typography>
      </Container>
    );
  }

  if (error && !sessionId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={initializeSession}>
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h3" gutterBottom align="center">
        Identity Verification
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((step) => (
          <Step key={step.step}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {activeStep < steps.length ? (
        <>
          {currentStep?.isIntro ? (
            <Paper elevation={2} sx={{ p: 4, maxWidth: 560, mx: 'auto' }}>
              <Typography variant="h4" gutterBottom align="center">
                Hello and welcome
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                To verify your identity we will need to capture your National ID (front and back) and a selfie.
                This usually takes about 2–3 minutes. You will be able to review and correct any details we read from your ID.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Please make sure you have your National ID with you and are in a well-lit place.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => setActiveStep(1)}
                >
                  Confirm, start now
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  component={Link}
                  to="/"
                >
                  I&apos;ll complete it later
                </Button>
              </Box>
            </Paper>
          ) : !showExtractedData ? (
            <DocumentCapture
              documentType={currentStep.documentType}
              onCapture={handleCapture}
              sessionId={sessionId}
              selfieSilhouetteUrl={currentStep.step === 'selfie' ? '/selfie-silhouette.png' : undefined}
            />
          ) : (
            <ExtractedDataDisplay
              extractedData={extractedData}
              documentId={uploadedDocument?.id}
              onCorrect={handleCorrections}
              onContinue={handleContinue}
              stepCode={currentStep?.step}
              capturedImageData={lastCapturedImageData}
              onRetake={handleRetake}
            />
          )}

          {loading && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Processing document...
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            ✅ KYC Complete!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Your documents have been submitted for verification.
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setActiveStep(0);
              initializeSession();
            }}
          >
            Start Over
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default KYCFlow;
